import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import type { Editor } from '@tiptap/core'
import type { PageDocument } from '@/lib/db/rxdb/hooks/entities/pages'
import { TiptapRichTextEditor } from '@/components/editor/TiptapRichTextEditor'
import type { EditorActions } from '@/components/editor/editorActions'
import { useCollaboration } from '@/components/editor/useCollaboration'
import { useEditorAutoSave } from '@/components/editor/useEditorAutoSave'
import { DEFAULT_FORMAT_TOOLBAR_ITEMS } from '@/components/editor/toolbarDefaults'
import {
  useEntityLinkMutations,
  useEntityLinks,
  usePageContent,
  usePageContentMutations,
} from '@/lib/db/rxdb/hooks'
import { ENTITY_TYPES } from '@/lib/constants/entityTypes'
import useLoginStore from '@/store/LoginStore'

const EDITOR_NAMESPACE = 'PageDescriptionEditor'

interface PageDescriptionEditorProps {
  page: PageDocument
  canEdit: boolean
  collaborative?: boolean
  userName?: string
  userColor?: string
  token?: string
  onAddComment?: (anchorId: string) => void
  onCommentClick?: (anchorId: string) => void
  onEditorReady?: (editor: Editor | null) => void
  actions?: EditorActions
}

/**
 * Full-page Tiptap editor for page content.
 * Supports both standalone (debounced saves via useEditorAutoSave) and collaborative (Y.js/Hocuspocus) modes.
 */
export function PageDescriptionEditor({
  page,
  canEdit,
  collaborative = false,
  userName = 'Anonymous',
  userColor,
  token,
  onAddComment,
  onCommentClick,
  onEditorReady,
  actions,
}: PageDescriptionEditorProps) {
  const { t } = useTranslation('projects')
  const { content } = usePageContent(page?.id)
  const { createPageContent, updatePageContent } = usePageContentMutations()
  const { links: existingLinks } = useEntityLinks(page?.id)
  const { createEntityLink, deleteEntityLink } = useEntityLinkMutations()
  const userId = useLoginStore((state) => state.userId)

  const { provider, ydoc } = useCollaboration({
    documentName: `page-${page?.id}`,
    userName,
    userColor,
    token,
    enabled: collaborative && !!page?.id,
  })

  const createContent = useCallback(
    async (data: { content: string; format: string; length: number; plainText: string; version: number }) => {
      if (!page) return
      await createPageContent({
        pageId: page.id,
        ...data,
      })
    },
    [page, createPageContent],
  )

  const updateContent = useCallback(
    async (id: string, data: { content: string; format: string; length: number; plainText: string; version: number }) => {
      await updatePageContent(id, data)
    },
    [updatePageContent],
  )

  const { displayContent, handleContentChange } = useEditorAutoSave({
    entityId: page?.id,
    entityType: ENTITY_TYPES.DOCUMENT,
    entityName: page?.title || '',
    envId: page?.envId,
    userId,
    content,
    existingLinks,
    canEdit,
    collaborative,
    createContent,
    updateContent,
    createEntityLink,
    deleteEntityLink,
  })

  const handleAddComment = useCallback(
    (anchorId: string) => {
      onAddComment?.(anchorId)
    },
    [onAddComment],
  )

  if (!page) {
    return null
  }

  return (
    <TiptapRichTextEditor
      key={collaborative ? `collab-${page.id}` : `${page.id}-${content?.id || 'no-content'}`}
      namespace={`${EDITOR_NAMESPACE}-${page.id}`}
      initialContent={collaborative ? undefined : displayContent}
      format={content?.format as 'tiptap' | 'markdown' | 'plain' | undefined}
      editable={canEdit}
      placeholder={t('docs.editorPlaceholder')}
      // Short, so Links and Subpages sit right after the content (design 32a).
      contentEditableClassName="py-0 min-h-7"
      placeholderClassName="top-0"
      onContentChange={collaborative ? undefined : handleContentChange}
      ariaLabel="Page content editor"
      toolbarItems={DEFAULT_FORMAT_TOOLBAR_ITEMS}
      enableSlashCommands
      enableMentions
      enableEntityLinks
      enableTables
      collaborative={collaborative}
      ydoc={ydoc}
      provider={provider}
      userName={userName}
      userColor={userColor}
      enableComments
      onAddComment={handleAddComment}
      onCommentClick={onCommentClick}
      onEditorReady={onEditorReady}
      actions={actions}
    />
  )
}
